import React, { useRef, useState, useEffect } from "react";
import { Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import dayjs from "~/config/dayjs";
import useDateFormatter from "~/hooks/useDateFormatter";
import "./ServiceExpiredCard.css";

const ServiceExpiredCard = ({
	expiredTimeLabel,
	dayLabel,
	errorMessage,
	loadingSlots,
	hasFeatureItems,
	slots = [],
	hasMoreSlotsThisDay,
	timeZone,
	onSelectSlot,
	onSeeAllThisDay,
	onSeeOtherDay,
	serviceName,
	advanceNoticeMinutes,
}) => {
	const { t } = useTranslation();
	const { formatTime } = useDateFormatter();
	const pillsContainerRef = useRef(null);
	const [pillFontSize, setPillFontSize] = useState(14);

	useEffect(() => {
		const el = pillsContainerRef.current;
		if (!el) return;
		const n = Math.min(slots.length, 3);
		if (n === 0) return;

		const calculate = (width) => {
			const gap = 9 * (n - 1);
			const pillWidth = (width - gap) / n;
			const availableForText = pillWidth - 28; // 28 = padding 14px * 2
			const computed = Math.floor(availableForText / 5.5); // ~5.5px per char
			setPillFontSize(Math.max(10, Math.min(14, computed)));
		};

		calculate(el.getBoundingClientRect().width);

		const observer = new ResizeObserver(([entry]) => {
			calculate(entry.contentRect.width);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [slots.length]);

	const renderPill = (slot, index) => {
		const label = formatTime(dayjs(slot.start).utc().tz(timeZone).format("HH:mm"));
		return (
			<div
				key={slot.start}
				onClick={() => onSelectSlot(slot)}
				style={{
					color: "#000000",
					backgroundColor: "#FFFFFF",
					border: "1px solid #D9D9D9",
					borderRadius: "10px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flex: 1,
					minWidth: 0,
					height: "40px",
					padding: "0 14px",
					cursor: "pointer",
					fontSize: `${pillFontSize}px`,
					fontWeight: 600,
					fontFamily: "CircularStd, sans-serif",
					animation: "serviceExpiredSlotFadeIn 0.3s ease-out backwards",
					animationDelay: `${Math.min(index, 6) * 0.05}s`,
				}}>
				{label}
			</div>
		);
	};

	return (
		<div
			style={{
				margin: "16px 0",
				background: "#FBF1EE",
				borderLeft: "4px solid #672E37",
				borderRadius: "5px 14px 14px 5px",
				padding: 16,
				animation: "serviceExpiredCardFadeIn 0.35s ease-out",
			}}>
			<Typography style={{ fontSize: 15, fontWeight: 700, color: "#222", lineHeight: 1.35, marginBottom: 6 }}>
				{errorMessage || t("advanceNoticeCardTitle", { time: expiredTimeLabel })}
			</Typography>
			{errorMessage ? (
				<Typography style={{ fontSize: 14, lineHeight: 1.5, color: "#6B5A56" }}>
					{t("professionalNoAgendaCardBody")}
				</Typography>
			) : (
				<Typography style={{ fontSize: 14, lineHeight: 1.5, color: "#6B5A56" }}>
					{t("advanceNoticeCardBody", { serviceName, minutes: advanceNoticeMinutes })}
				</Typography>
			)}

			{loadingSlots ? (
				<Typography style={{ fontSize: 13, color: "#9B928C", marginTop: 12 }}>{t("advanceNoticeLoading")}</Typography>
			) : (
				<>
					{hasFeatureItems && slots.length > 0 && (
						<>
							<div ref={pillsContainerRef} style={{ display: "flex", gap: 9, marginTop: 14, flexWrap: "nowrap" }}>
								{slots.map((slot, index) => renderPill(slot, index))}
							</div>
							{hasMoreSlotsThisDay && onSeeAllThisDay && (
								<button
									type="button"
									onClick={onSeeAllThisDay}
									style={{
										display: "inline-block",
										marginTop: 13,
										fontSize: 14,
										fontWeight: 700,
										color: "#672E37",
										textDecoration: "underline",
										textUnderlineOffset: "3px",
										cursor: "pointer",
										background: "none",
										border: 0,
										padding: 0,
										fontFamily: "inherit",
									}}>
									{t("advanceNoticeSeeAllLink", { day: dayLabel })}
								</button>
							)}
						</>
					)}

					{(hasFeatureItems || errorMessage) && slots.length === 0 && onSeeOtherDay && (
						<button
							type="button"
							onClick={onSeeOtherDay}
							style={{
								marginTop: 14,
								width: "100%",
								background: "#672E37",
								color: "#fff",
								border: 0,
								borderRadius: 20,
								padding: "12px 16px",
								fontSize: 14,
								fontWeight: 700,
								cursor: "pointer",
								fontFamily: "inherit",
							}}>
							{t("advanceNoticeSeeOtherDayCta")}
						</button>
					)}
				</>
			)}
		</div>
	);
};

export default ServiceExpiredCard;
